/**
 * The rAF hover-tilt / sheen system (INVENTORY-DESKTOP §6.4).
 *
 * Spec source: `design/Result Tile.dc.html:59-122` (the readable reference) and
 * the app template's own `tile()` + `hvStep` (`design/Games Tonight.dc.html`
 * 1194-1201 / 1414-1431). Both are byte-identical maths; the constants below
 * are the prototype literals and must not be "tidied".
 *
 * PERFORMANCE CONTRACT (INVENTORY-DESKTOP §10.5 risk 1). The prototype called
 * `this.setState({hv})` from the root component every frame, re-rendering ~60
 * Discover tiles at 60fps. This port keeps the *observable* behaviour and drops
 * the cost:
 *   - ONE module-level rAF for the whole tree, started on the first subscriber
 *     and cancelled when the last one unmounts (no permanently-running loop);
 *   - ONE hovered tile at a time (a module singleton, exactly like the
 *     prototype's single `state.hv`), so switching tiles still SNAPS the old
 *     one home in one frame;
 *   - every write goes straight to `element.style` through a ref. Nothing in
 *     this module ever calls `setState`, so hovering never re-renders React.
 *
 * The pure maths (`tiltTransform`, `tiltShadow`, `tiltZ`, `sheenGradient`,
 * `tiltStep`) is exported and unit-tested in `useHoverTilt.test.ts` — that is
 * the part a port can silently drift on.
 *
 * USAGE (lanes D2 / D3):
 *
 *   const tilt = useHoverTilt(g.id);
 *   <div ref={tilt.ref} {...tilt.handlers} style={{ ...myStyle, ...tilt.rest }}>
 *     …cover, ribbons, scrim…
 *     <div ref={tilt.sheenRef} style={SHEEN_LAYER} />   // background is driven
 *     …caption…
 *   </div>
 *
 * `tilt.rest` is the prog-0 transform/box-shadow/z-index; apply it so the tile
 * is correct on first paint and after a snap. `SHEEN_LAYER` is exported below
 * (`position:absolute;inset:0;mix-blend-mode:screen;pointer-events:none`) with
 * the resting gradient already in it. See `HoverTile.tsx` for the common case.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';

// ---------------------------------------------------------------------------
// constants — prototype literals, INVENTORY-DESKTOP §6.4
// ---------------------------------------------------------------------------

/** per-frame lerp factor */
export const TILT_K = 0.16;
/** below this total delta the loop stops writing (prototype stopped setState) */
export const TILT_SETTLE = 0.0005;
export const TILT_PERSPECTIVE_PX = 650;
/** max rotation on each axis, in degrees, at full hover */
export const TILT_DEG = 9;
export const TILT_SCALE_MAX = 1.05;
/** `sheenIntensity` prop default, as a percentage (Result Tile data-props) */
export const SHEEN_INTENSITY_DEFAULT = 45;
/** `hovZ` flips to 5 once hover progress passes this */
export const TILT_Z_THRESHOLD = 0.02;

/** A tile identity. Game ids are `number | string`; prefix them when two
 *  mounted tiles could share one (e.g. `'todo:' + id` for the GOTY featured
 *  cover). Ids must be unique among simultaneously mounted tilted elements. */
export type TiltId = string | number;

export interface TiltState {
  id: TiltId;
  mx: number;
  my: number;
  prog: number;
}

export interface TiltTarget {
  id: TiltId;
  mx: number;
  my: number;
  /** 1 while the pointer is inside, 0 once it leaves */
  hov: number;
}

const REST: Omit<TiltState, 'id'> = { mx: 0.5, my: 0.5, prog: 0 };

// ---------------------------------------------------------------------------
// pure maths
// ---------------------------------------------------------------------------

const lerpAt = (prog: number) => (a: number, b: number) => a + (b - a) * prog;

/** `hovT` — perspective + two rotations + scale, all `.toFixed`-rounded. */
export function tiltTransform(mx: number, my: number, prog: number): string {
  const lerp = lerpAt(prog);
  return (
    'perspective(' +
    TILT_PERSPECTIVE_PX +
    'px) rotateY(' +
    ((mx - 0.5) * TILT_DEG * prog).toFixed(2) +
    'deg) rotateX(' +
    ((0.5 - my) * TILT_DEG * prog).toFixed(2) +
    'deg) scale(' +
    lerp(1, TILT_SCALE_MAX).toFixed(3) +
    ')'
  );
}

/** `hovSh` — the drop shadow slides opposite the tilt, plus a 1px rim light. */
export function tiltShadow(mx: number, my: number, prog: number): string {
  const lerp = lerpAt(prog);
  return (
    ((0.5 - mx) * 18 * prog).toFixed(1) +
    'px ' +
    lerp(2, 8 + (0.5 - my) * 14).toFixed(1) +
    'px ' +
    lerp(6, 28).toFixed(0) +
    'px rgba(0,0,0,' +
    lerp(0.35, 0.65).toFixed(2) +
    '), 0 0 0 1px rgba(255,255,255,' +
    (0.12 * prog).toFixed(3) +
    ')'
  );
}

/** `hovZ` — lift the hovered tile over its grid neighbours. */
export function tiltZ(prog: number): number {
  return prog > TILT_Z_THRESHOLD ? 5 : 1;
}

/**
 * `sheen` — a Steam-style gloss on a fixed top-right light. `t` is the tilt
 * toward that light, `E` the leading edge of the highlight along the 225deg
 * diagonal, and the six stops fill the corner, peak just shy of `E`, cut off
 * sharply past it and decay back to transparent.
 *
 * `intensity` is the Result Tile `sheenIntensity` prop as a percentage; the app
 * hard-codes 45 (`* 0.45`), which is this module's default.
 */
export function sheenGradient(
  mx: number,
  my: number,
  prog: number,
  intensity: number = SHEEN_INTENSITY_DEFAULT,
): string {
  const lerp = lerpAt(prog);
  const t = (mx + (1 - my)) / 2;
  const E = 10 + t * 15;
  const st = (alpha: number, pos: number) => 'rgba(255,255,255,' + alpha.toFixed(3) + ') ' + pos.toFixed(1) + '%';
  const k = intensity / 100;
  const a = lerp(0.2, 0.2 + 0.22 * (1 - Math.abs(t - 0.5) * 2)) * k;
  return (
    'linear-gradient(225deg, ' +
    [st(a * 0.35, 0), st(a * 0.55, E * 0.5), st(a * 0.85, E * 0.82), st(a, E), st(a * 0.25, E + 2.5), st(0, E + 4)].join(
      ', ',
    ) +
    ')'
  );
}

/**
 * One frame of the lerp. `changed` is the prototype's settle test; when the
 * hovered id changes the frame SNAPS (centre + prog 0) instead of lerping,
 * which is what makes moving the pointer between two tiles drop the first one
 * instantly rather than trailing it home.
 */
export function tiltStep(cur: TiltState, tgt: TiltTarget, k: number = TILT_K): { next: TiltState; changed: boolean } {
  const snap = cur.id !== tgt.id;
  const nx = snap ? 0.5 : cur.mx + (tgt.mx - cur.mx) * k;
  const ny = snap ? 0.5 : cur.my + (tgt.my - cur.my) * k;
  const np = snap ? 0 : cur.prog + (tgt.hov - cur.prog) * k;
  const changed =
    snap || Math.abs(nx - cur.mx) + Math.abs(ny - cur.my) + Math.abs(np - cur.prog) > TILT_SETTLE;
  return { next: { id: tgt.id, mx: nx, my: ny, prog: np }, changed };
}

/** The prog-0 style every tilted element wears at rest. */
export const REST_TRANSFORM = tiltTransform(REST.mx, REST.my, REST.prog);
export const REST_SHADOW = tiltShadow(REST.mx, REST.my, REST.prog);
export const REST_SHEEN = sheenGradient(REST.mx, REST.my, REST.prog);

export const REST_STYLE: CSSProperties = {
  transform: REST_TRANSFORM,
  boxShadow: REST_SHADOW,
  zIndex: 1,
};

/**
 * Drop this on the sheen overlay div. The `background` is overwritten by the
 * loop; the value here is the resting gradient so the layer is correct before
 * the first frame and while nothing is hovered.
 */
export const SHEEN_LAYER: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: REST_SHEEN,
  mixBlendMode: 'screen',
  pointerEvents: 'none',
};

// ---------------------------------------------------------------------------
// the module singleton — one rAF, one hovered tile
// ---------------------------------------------------------------------------

interface TiltNode {
  el: HTMLElement | null;
  sheen: HTMLElement | null;
  intensity: number;
}

const nodes = new Map<TiltId, TiltNode>();

let cur: TiltState = { id: 0, ...REST };
let tgt: TiltTarget = { id: 0, ...REST, hov: 0 };
let raf = 0;
let subscribers = 0;

function paint(id: TiltId, mx: number, my: number, prog: number): void {
  const n = nodes.get(id);
  if (!n) return;
  if (n.el) {
    n.el.style.transform = tiltTransform(mx, my, prog);
    n.el.style.boxShadow = tiltShadow(mx, my, prog);
    n.el.style.zIndex = String(tiltZ(prog));
  }
  if (n.sheen) n.sheen.style.background = sheenGradient(mx, my, prog, n.intensity);
}

function paintRest(id: TiltId): void {
  paint(id, REST.mx, REST.my, REST.prog);
}

function frame(): void {
  const { next, changed } = tiltStep(cur, tgt);
  if (changed) {
    if (next.id !== cur.id) paintRest(cur.id);
    cur = next;
    paint(cur.id, cur.mx, cur.my, cur.prog);
  }
  raf = requestAnimationFrame(frame);
}

function startLoop(): void {
  if (raf) return;
  raf = requestAnimationFrame(frame);
}

function stopLoop(): void {
  if (!raf) return;
  cancelAnimationFrame(raf);
  raf = 0;
}

/**
 * Abandon any in-flight tilt and settle the current tile immediately. The
 * prototype does exactly this at the top of `ghostDragStart` (line 1630:
 * `this._hvT = { id: 0, mx: .5, my: .5, hov: 0 }`) so a capsule does not stay
 * lifted while its ghost is being dragged.
 */
export function cancelHoverTilt(): void {
  tgt = { id: 0, ...REST, hov: 0 };
  paintRest(cur.id);
  cur = { id: 0, ...REST };
}

/** Test seam — clears the registry, the loop and both states. */
export function resetHoverTiltModule(): void {
  stopLoop();
  subscribers = 0;
  nodes.clear();
  cur = { id: 0, ...REST };
  tgt = { id: 0, ...REST, hov: 0 };
}

/** Current smoothed state — for tests and debugging only. */
export function hoverTiltState(): Readonly<TiltState> {
  return cur;
}

// ---------------------------------------------------------------------------
// the hook
// ---------------------------------------------------------------------------

export interface HoverTiltHandlers {
  onMouseEnter: () => void;
  onMouseMove: (ev: ReactMouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
}

export interface HoverTiltHandle {
  /** ref for the element that receives transform / box-shadow / z-index */
  ref: (el: HTMLElement | null) => void;
  /** ref for the `mix-blend-mode:screen` overlay that receives the gradient */
  sheenRef: (el: HTMLElement | null) => void;
  /** spread onto the same element as `ref` */
  handlers: HoverTiltHandlers;
  /** prog-0 transform/box-shadow/z-index — merge into the element's style */
  rest: CSSProperties;
  /** prog-0 sheen layer style — merge into the overlay's style */
  restSheen: CSSProperties;
}

/**
 * Wire one tile into the shared tilt loop.
 *
 * `id` must be stable and unique among mounted tilted elements (see `TiltId`).
 * `intensity` is the sheen strength as a percentage; leave it alone unless a
 * design call says otherwise.
 */
export function useHoverTilt(id: TiltId, intensity: number = SHEEN_INTENSITY_DEFAULT): HoverTiltHandle {
  const node = useRef<TiltNode>({ el: null, sheen: null, intensity });
  node.current.intensity = intensity;

  // Registry + loop lifetime. Re-runs when `id` changes (a keyed list should
  // not do that, but a filtered board can reuse a component instance).
  useEffect(() => {
    nodes.set(id, node.current);
    subscribers += 1;
    startLoop();
    return () => {
      nodes.delete(id);
      subscribers -= 1;
      if (subscribers <= 0) {
        subscribers = 0;
        stopLoop();
      }
    };
  }, [id]);

  const ref = useCallback(
    (el: HTMLElement | null) => {
      node.current.el = el;
      // A freshly mounted element must not inherit the previous occupant's
      // lifted transform (React reuses DOM nodes across list re-orders).
      if (el && cur.id !== id) {
        el.style.transform = REST_TRANSFORM;
        el.style.boxShadow = REST_SHADOW;
        el.style.zIndex = '1';
      }
    },
    [id],
  );

  const sheenRef = useCallback(
    (el: HTMLElement | null) => {
      node.current.sheen = el;
      if (el && cur.id !== id) el.style.background = sheenGradient(REST.mx, REST.my, REST.prog, node.current.intensity);
    },
    [id],
  );

  const handlers = useMemo<HoverTiltHandlers>(
    () => ({
      // The prototype RESETS to centre on enter rather than reading the entry
      // point — the tile always tilts out from flat, never from an edge.
      onMouseEnter: () => {
        tgt = { id, mx: 0.5, my: 0.5, hov: 1 };
      },
      onMouseMove: (ev) => {
        if (tgt.id !== id) return;
        const r = ev.currentTarget.getBoundingClientRect();
        if (!r.width || !r.height) return;
        tgt.mx = (ev.clientX - r.left) / r.width;
        tgt.my = (ev.clientY - r.top) / r.height;
      },
      onMouseLeave: () => {
        if (tgt.id === id) tgt = { id, mx: 0.5, my: 0.5, hov: 0 };
      },
    }),
    [id],
  );

  const restSheen = useMemo<CSSProperties>(
    () => ({ ...SHEEN_LAYER, background: sheenGradient(REST.mx, REST.my, REST.prog, intensity) }),
    [intensity],
  );

  return { ref, sheenRef, handlers, rest: REST_STYLE, restSheen };
}
